import React, { useId } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "@mui/material";
import { InsightsResponse, Platform } from "../api/v1/api_pb";
import { timestampDate } from "@bufbuild/protobuf/wkt";
import moment from "moment/moment";

interface ComputeUsageProps {
  readonly insights: InsightsResponse;
  readonly dateFormat: string;
  readonly platforms: readonly Platform[];
}

function ComputeUsage(props: ComputeUsageProps) {
  const theme = useTheme();
  const colorId = useId();

  const data = props.insights.aggregates.map((aggregate) => {
    const secondsUsed = aggregate.platformMetrics
      .filter((metrics) => props.platforms.includes(metrics.platform))
      .reduce((accumulator, metrics) => {
        return accumulator + (metrics.computeUsageSeconds?.sum ?? 0);
      }, 0);

    return {
      date: moment(timestampDate(aggregate.startAt!)).format(props.dateFormat),
      "Minutes used": moment.duration(secondsUsed, "seconds").asMinutes(),
    };
  });

  return (
    <ResponsiveContainer width="100%" height={350}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id={colorId} x1="0" y1="0" x2="0" y2="1">
            <stop
              offset="5%"
              stopColor={theme.palette.primary.main}
              stopOpacity={0.8}
            />
            <stop
              offset="95%"
              stopColor={theme.palette.primary.main}
              stopOpacity={0}
            />
          </linearGradient>
        </defs>
        <XAxis dataKey="date" />
        <YAxis />
        <CartesianGrid strokeDasharray="3 3" />
        <Tooltip
          contentStyle={{ backgroundColor: theme.palette.background.paper }}
          formatter={(value) => Number(value).toFixed(0)}
        />
        <Area
          type="monotone"
          dataKey="Minutes used"
          stroke={theme.palette.primary.main}
          fillOpacity={1}
          fill={`url(#${colorId})`}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default ComputeUsage;
